class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));

    if (!this.viewport || !this.slides.length) return;

    this.currentIndex = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayFrequency = Number(this.dataset.autoplayFrequency) || 5000;
    this.autoplayTimer = null;
    this.isInteracting = false;

    this.handleResize = this.handleResize.bind(this);
    this.resizeObserver = window.ResizeObserver ? new ResizeObserver(this.handleResize) : null;
    if (this.resizeObserver) this.resizeObserver.observe(this);
    window.addEventListener('resize', this.handleResize);

    requestAnimationFrame(() => this.showSlide(0, { skipTransition: true }));

    if (this.slides.length > 1) {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.currentIndex - 1, 'backward'));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.currentIndex + 1, 'forward'));
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.addEventListener('pointerenter', () => this.pauseAutoplay());
      this.addEventListener('pointerleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', () => this.resumeAutoplay());

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pauseTimerOnly();
        } else if (!this.isInteracting) {
          this.startAutoplay();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      let touchStartX = 0;
      this.addEventListener('touchstart', (event) => {
        touchStartX = event.touches[0].clientX;
      }, { passive: true });

      this.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) > 40) {
          const forward = delta < 0;
          this.goTo(this.currentIndex + (forward ? 1 : -1), forward ? 'forward' : 'backward');
        }
      }, { passive: true });

      this.startAutoplay();
    }

    // Not gated behind window.Shopify.designMode: this custom element can upgrade
    // before the theme editor sets that flag, which would otherwise permanently
    // skip attaching this listener. The event only ever fires inside the editor.
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.pauseTimerOnly();
    if (this.exitTimer) window.clearTimeout(this.exitTimer);
    window.removeEventListener('resize', this.handleResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  handleBlockSelect(event) {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index > -1) {
      this.pauseAutoplay();
      this.goTo(index);
    }
  }

  handleResize() {
    this.updateHeight(true);
  }

  inferDirection(from, to, total) {
    if (from === to) return 'forward';
    const forwardDistance = (to - from + total) % total;
    const backwardDistance = (from - to + total) % total;
    return forwardDistance <= backwardDistance ? 'forward' : 'backward';
  }

  goTo(index, direction) {
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    const resolvedDirection = direction || this.inferDirection(this.currentIndex, next, total);
    this.showSlide(next, { direction: resolvedDirection });
  }

  updateDots(index) {
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  showSlide(index, { skipTransition = false, direction = 'forward' } = {}) {
    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    this.updateDots(index);

    if (skipTransition || previousIndex === index) {
      this.slides.forEach((slide, i) => {
        slide.classList.remove('is-entering', 'is-exiting');
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      this.updateHeight(true);
      return;
    }

    this.classList.toggle('is-previous-direction', direction === 'backward');

    const previousSlide = this.slides[previousIndex];
    const currentSlide = this.slides[index];

    this.slides.forEach((slide, i) => {
      if (i !== previousIndex && i !== index) {
        slide.classList.remove('is-active', 'is-entering', 'is-exiting');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    if (currentSlide) {
      currentSlide.classList.remove('is-active', 'is-exiting');
      currentSlide.classList.add('is-entering');
      currentSlide.setAttribute('aria-hidden', 'false');

      // Force layout so the browser registers the entering position before
      // switching to is-active, otherwise the slide-in transition won't run.
      void currentSlide.offsetHeight;

      currentSlide.classList.remove('is-entering');
      currentSlide.classList.add('is-active');
    }

    if (previousSlide) {
      previousSlide.classList.remove('is-active');
      previousSlide.classList.add('is-exiting');
      previousSlide.setAttribute('aria-hidden', 'true');

      if (this.exitTimer) window.clearTimeout(this.exitTimer);
      this.exitTimer = window.setTimeout(() => {
        previousSlide.classList.remove('is-exiting');
        this.exitTimer = null;
      }, 620);
    }

    this.updateHeight(false);
  }

  updateHeight(skipTransition) {
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;

    const panel = activeSlide.querySelector('[data-content]');
    const wrapper = activeSlide.querySelector('.hero-slider__content');
    const wrapperStyles = wrapper ? window.getComputedStyle(wrapper) : null;
    const verticalPadding = wrapperStyles
      ? (parseFloat(wrapperStyles.paddingTop) || 0) + (parseFloat(wrapperStyles.paddingBottom) || 0)
      : 0;
    const contentHeight = panel ? panel.scrollHeight + verticalPadding : 0;
    const viewportWidth = this.viewport.getBoundingClientRect().width;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const ratioHeight = viewportWidth * (isMobile ? 3 / 2 : 5 / 12);
    const targetHeight = Math.max(contentHeight, ratioHeight);

    if (skipTransition) {
      this.viewport.style.transition = 'none';
      this.viewport.style.height = `${targetHeight}px`;
      requestAnimationFrame(() => {
        this.viewport.style.transition = '';
      });
    } else {
      this.viewport.style.height = `${targetHeight}px`;
    }
  }

  startAutoplay() {
    this.pauseTimerOnly();

    if (!this.autoplayEnabled || this.isInteracting || this.slides.length <= 1) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.goTo(this.currentIndex + 1, 'forward');
      this.startAutoplay();
    }, this.autoplayFrequency);
  }

  pauseTimerOnly() {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.isInteracting = true;
    this.pauseTimerOnly();
  }

  resumeAutoplay() {
    this.isInteracting = false;
    this.startAutoplay();
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
